// src/hooks/useComplianceRealtime.ts
//
// Subscribe to COMPLIANCE_* realtime events for a workspace and
// invalidate the relevant RTK Query tags so dashboards, inbox lists,
// and per-subject views refresh without a manual refetch.
//
// Safe to mount from multiple surfaces (Compliance page, Contact
// section, Circle tab, Provider profile) — realtimeClient refcounts
// the topic subscribe.

import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { realtimeClient } from '../realtime/socketClient';
import {
  RealtimeEventType,
  type RealtimeEventTypeValue,
} from '../realtime/events';
import { complianceApi } from '../api/complianceApi';
import type { ComplianceRealtimePayload, ComplianceSubjectType } from '../types/api';

interface UseComplianceRealtimeOptions {
  workspaceId?: string | null;
  /** Optional: current focus so we can invalidate its subject slice. */
  subjectType?: ComplianceSubjectType | null;
  subjectId?: string | null;
  onEvent?: (payload: ComplianceRealtimePayload, kind: string) => void;
}

// Which surfaces care about each event. Kept explicit so a future
// refactor can't silently drop an invalidation.
const COMPLIANCE_EVENT_MAP: Array<[RealtimeEventTypeValue, string]> = [
  [RealtimeEventType.COMPLIANCE_REQUIREMENT_CREATED, 'requirement_created'],
  [RealtimeEventType.COMPLIANCE_REQUIREMENT_UPDATED, 'requirement_updated'],
  [RealtimeEventType.COMPLIANCE_ASSIGNMENT_CREATED, 'assignment_created'],
  [RealtimeEventType.COMPLIANCE_ASSIGNMENT_UPDATED, 'assignment_updated'],
  [RealtimeEventType.COMPLIANCE_DOCUMENT_CREATED, 'document_created'],
  [RealtimeEventType.COMPLIANCE_DOCUMENT_UPDATED, 'document_updated'],
  [RealtimeEventType.COMPLIANCE_APPROVAL_REQUESTED, 'approval_requested'],
  [RealtimeEventType.COMPLIANCE_APPROVED, 'approved'],
  [RealtimeEventType.COMPLIANCE_REJECTED, 'rejected'],
  [RealtimeEventType.COMPLIANCE_EXPIRING, 'expiring'],
  [RealtimeEventType.COMPLIANCE_EXPIRED, 'expired'],
  [RealtimeEventType.COMPLIANCE_RENEWED, 'renewed'],
];

type ComplianceTagType =
  | 'ComplianceCategory'
  | 'ComplianceRequirement'
  | 'ComplianceAssignment'
  | 'ComplianceDocument'
  | 'ComplianceApproval'
  | 'ComplianceReview'
  | 'ComplianceEligibility'
  | 'ComplianceCoverage';

export function useComplianceRealtime({
  workspaceId,
  subjectType,
  subjectId,
  onEvent,
}: UseComplianceRealtimeOptions) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useDispatch<any>();

  useEffect(() => {
    if (!workspaceId) return;
    const topic = `workspace:${workspaceId}`;
    realtimeClient.subscribe(topic);

    const invalidate = (
      payload: ComplianceRealtimePayload,
      kind: string,
    ) => {
      const tags: Array<{ type: ComplianceTagType; id: string }> = [
        // Coverage summary always changes on any compliance write
        // that could shift any subject's status.
        { type: 'ComplianceCoverage', id: 'SUMMARY' },
        { type: 'ComplianceCoverage', id: 'REQUIREMENTS' },
      ];

      if (kind.startsWith('requirement_')) {
        tags.push({ type: 'ComplianceRequirement', id: 'LIST' });
        if (payload.requirementId) {
          tags.push({ type: 'ComplianceRequirement', id: payload.requirementId });
        }
      }

      if (kind.startsWith('assignment_')) {
        tags.push({ type: 'ComplianceAssignment', id: 'LIST' });
        if (payload.assignmentId) {
          tags.push({ type: 'ComplianceAssignment', id: payload.assignmentId });
        }
        if (payload.subjectType && payload.subjectId) {
          tags.push({
            type: 'ComplianceAssignment',
            id: `subject-${payload.subjectType}-${payload.subjectId}`,
          });
          tags.push({
            type: 'ComplianceEligibility',
            id: `${payload.subjectType}-${payload.subjectId}`,
          });
          tags.push({
            type: 'ComplianceCoverage',
            id: `subject-${payload.subjectType}`,
          });
        }
      }

      if (
        kind === 'document_created' ||
        kind === 'document_updated' ||
        kind === 'approval_requested' ||
        kind === 'approved' ||
        kind === 'rejected' ||
        kind === 'expiring' ||
        kind === 'expired' ||
        kind === 'renewed'
      ) {
        tags.push({ type: 'ComplianceDocument', id: 'LIST' });
        if (payload.documentId) {
          tags.push({ type: 'ComplianceDocument', id: payload.documentId });
          tags.push({ type: 'ComplianceApproval', id: `doc-${payload.documentId}` });
        }
        if (payload.predecessorId) {
          tags.push({ type: 'ComplianceDocument', id: payload.predecessorId });
        }
        if (payload.successorId) {
          tags.push({ type: 'ComplianceDocument', id: payload.successorId });
        }
        if (payload.subjectType && payload.subjectId) {
          tags.push({
            type: 'ComplianceDocument',
            id: `subject-${payload.subjectType}-${payload.subjectId}`,
          });
          tags.push({
            type: 'ComplianceEligibility',
            id: `${payload.subjectType}-${payload.subjectId}`,
          });
          tags.push({
            type: 'ComplianceCoverage',
            id: `subject-${payload.subjectType}`,
          });
        }
      }

      // Belt-and-suspenders: invalidate the caller's focused subject
      // in case the payload omits it (e.g., requirement-scope change
      // that indirectly affects many subjects).
      if (subjectType && subjectId) {
        tags.push({
          type: 'ComplianceEligibility',
          id: `${subjectType}-${subjectId}`,
        });
        tags.push({
          type: 'ComplianceDocument',
          id: `subject-${subjectType}-${subjectId}`,
        });
        tags.push({
          type: 'ComplianceAssignment',
          id: `subject-${subjectType}-${subjectId}`,
        });
      }

      dispatch(complianceApi.util.invalidateTags(tags));
      onEvent?.(payload, kind);
    };

    const listeners = COMPLIANCE_EVENT_MAP.map(([eventType, kind]) =>
      realtimeClient.on<ComplianceRealtimePayload>(
        topic,
        (payload) => invalidate(payload, kind),
        eventType,
      ),
    );

    return () => {
      for (const off of listeners) off();
      realtimeClient.unsubscribe(topic);
    };
  }, [workspaceId, subjectType, subjectId, dispatch, onEvent]);
}

export default useComplianceRealtime;
